import { InfoCard } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingApiRef } from '../../api/OnboardingApi';
import type { OnboardingState } from '../../types';

const PRACTICE_LABELS: Record<string, string> = {
  ai: 'AI',
  cloud: 'Cloud',
  data: 'Data',
  digital: 'Digital',
  engineering: 'Engineering',
  finlabs: 'FinLabs',
};

export const OnboardingCard = () => {
  const onboardingApi = useApi(onboardingApiRef);
  const configApi = useApi(configApiRef);
  const navigate = useNavigate();
  const [state, setState] = useState<OnboardingState | null>(null);

  useEffect(() => {
    onboardingApi.getState().then(setState);
  }, [onboardingApi]);

  useEffect(() => {
    if (state && !state.hasSeenOnboarding && !state.isDismissed) {
      navigate('/onboarding');
    }
  }, [state, navigate]);

  if (!state || state.isDismissed) {
    return null;
  }

  const practiceLabel = state.selectedPractice
    ? PRACTICE_LABELS[state.selectedPractice] ?? state.selectedPractice
    : null;

  return (
    <InfoCard title="Getting Started" noPadding>
      <Box sx={{ p: 2 }}>
        {practiceLabel ? (
          <>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {practiceLabel} Practice onboarding
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Complete your onboarding checklist to get set up at Synechron.
          </Typography>
        )}
        {state.hasCompletedSetup &&
          state.selectedPractice &&
          (() => {
            const checkedCount = (
              state.checkedItems[state.selectedPractice] ?? []
            ).length;
            const practiceConfig = configApi.getOptionalConfig(
              `newStarterOnboarding.practices.${state.selectedPractice}`,
            );
            const total = practiceConfig
              ? practiceConfig.getConfigArray('items').length
              : 0;
            const progress =
              total > 0 ? Math.round((checkedCount / total) * 100) : 0;
            return (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  aria-label={`Onboarding progress: ${checkedCount} of ${total} items complete`}
                />
                <Typography variant="caption">
                  {checkedCount} of {total} item{total !== 1 ? 's' : ''}{' '}
                  completed
                </Typography>
              </Box>
            );
          })()}
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate('/onboarding')}
          aria-label="Open onboarding checklist"
        >
          {state.hasCompletedSetup ? 'View Checklist' : 'Get Started'}
        </Button>
      </Box>
    </InfoCard>
  );
};
